import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Truck, Pencil } from 'lucide-react';

export const ReviewOrderDialog = ({
  reviewOrder, onClose,
  getStatusBadge, fmtDate, fmtDateTime,
  productDiscountByPid,
  masterPriceByPid = {},
  virtualPidSet = new Set(),
  canDispatchOrder, openDispatchDialog, openEditDialog,
  isStrictAdmin = false,
}) => (
  <Dialog open={!!reviewOrder} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="max-w-lg" data-testid="review-order-dialog">
      <DialogHeader>
        <DialogTitle>Order Details - #{reviewOrder?.id?.slice(0, 8)}</DialogTitle>
      </DialogHeader>
      {reviewOrder && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
              <p className="text-xs" style={{ color: '#5C6B61' }}>Dealer</p>
              <p className="font-medium text-sm">{reviewOrder.dealer_name}</p>
              <p className="text-xs" style={{ color: '#5C6B61' }}>{reviewOrder.dealer_phone}</p>
            </div>
            <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
              <p className="text-xs" style={{ color: '#5C6B61' }}>Status</p>
              <div className="flex gap-2 mt-1">
                {getStatusBadge(reviewOrder.status)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
              <p className="text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Order Date</p>
              <p className="text-sm">{new Date(reviewOrder.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              {reviewOrder.created_by_name && (
                <p className="text-[11px] mt-1" style={{ color: '#5C6B61' }} data-testid="review-created-by">
                  by <span className="font-semibold" style={{ color: '#2B5339' }}>{reviewOrder.created_by_name}</span>
                  {reviewOrder.created_by_role && reviewOrder.created_by_role !== 'dealer' && (
                    <span className="ml-1" style={{ color: '#8B96A0' }}>({reviewOrder.created_by_role})</span>
                  )}
                </p>
              )}
            </div>
            {reviewOrder.delivery_date ? (
              <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                <p className="text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Delivery Schedule</p>
                <p className="text-sm">{fmtDate(reviewOrder.delivery_date)} at {reviewOrder.delivery_time}</p>
              </div>
            ) : (
              <div className="p-3 rounded" style={{ background: '#FBF9F6' }}>
                <p className="text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Delivery Schedule</p>
                <p className="text-sm" style={{ color: '#B9BEB6' }}>Not scheduled</p>
              </div>
            )}
          </div>
          {reviewOrder.manual_description && (
            <div className="px-1 text-left" data-testid="review-manual-description">
              <p className="text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Description</p>
              <p className="text-sm whitespace-pre-wrap font-medium" style={{ color: '#C05C35' }}>
                {reviewOrder.manual_description}
              </p>
            </div>
          )}
          {reviewOrder.vehicle_number && (
            <div className="p-3 rounded" style={{ background: '#FBF9F6' }} data-testid="review-dispatch-info">
              <p className="text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Dispatch Info</p>
              {(() => {
                const latest = (reviewOrder.dispatches || []).reduce((m, d) => {
                  const t = d?.dispatched_at || '';
                  return t > m ? t : m;
                }, '');
                const dispAt = latest || reviewOrder.updated_at || '';
                return (
                  <>
                    <p className="text-sm">Vehicle: {reviewOrder.vehicle_number} | Driver: {reviewOrder.driver_mobile || '-'}</p>
                    {dispAt && (
                      <p className="text-sm mt-1" style={{ color: '#C05C35' }}>Dispatched on: <span className="font-medium">{fmtDateTime(dispAt)}</span></p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: '#5C6B61' }}>Items</p>
            {reviewOrder.items.map((item, idx) => {
              // Per-item override classification:
              //   - "virtual" → no real catalog rate exists for the product.
              //   - "delta"   → master rate exists but operator entered a
              //                 different number; show "Master ₹X → ₹Y".
              const isVirtual = item.product_id && virtualPidSet.has(item.product_id);
              const master = masterPriceByPid[item.product_id];
              const paid = Number(item.price);
              const hasMasterDelta = !isVirtual
                && master !== undefined
                && Number.isFinite(paid)
                && Math.abs(paid - master) > 0.005;
              const overrideKind = isVirtual ? 'virtual' : (hasMasterDelta ? 'delta' : null);
              return (
                <div key={idx} className="flex justify-between gap-2 py-2 text-sm" style={{ borderBottom: '1px solid #E2DFD8' }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="break-words">{item.product_name} x {item.quantity} @ {'\u20B9'}{item.price}</span>
                      {overrideKind && (
                        <span
                          className="inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                          style={{ background: '#C05C35', color: 'white', letterSpacing: '0.03em' }}
                          title={overrideKind === 'virtual'
                            ? 'Virtual product — operator-entered rate (no master catalog price)'
                            : `Master \u20B9${master?.toFixed(2)} \u2192 Order \u20B9${paid.toFixed(2)}`}
                          data-testid={`review-item-ovr-${idx}`}
                        >
                          {'\u20B9 OVR'}
                        </span>
                      )}
                    </div>
                    {overrideKind === 'delta' && (
                      <div className="text-[10px] mt-0.5" style={{ color: '#C05C35' }} data-testid={`review-item-delta-${idx}`}>
                        Master {'\u20B9'}{master.toFixed(2)} {'\u2192'} {'\u20B9'}{paid.toFixed(2)} ({paid > master ? '+' : ''}{(paid - master).toFixed(2)})
                      </div>
                    )}
                  </div>
                  <span className="font-medium whitespace-nowrap">{'\u20B9'}{item.total}</span>
                </div>
              );
            })}
            {(() => {
              const orderPct = reviewOrder.discount_percent == null ? 10 : reviewOrder.discount_percent;
              let net = 0;
              (reviewOrder.items || []).forEach(it => {
                const pct = it.discount_percent != null ? Number(it.discount_percent) : (productDiscountByPid[it.product_id] ?? orderPct);
                net += Number(it.total || 0) * (1 - pct / 100);
              });
              const gross = Number(reviewOrder.total_amount || 0);
              const discAmt = gross - net;
              const effPct = gross > 0 ? (discAmt / gross) * 100 : 0;
              return (
                <>
                  <div className="flex justify-between py-2 text-sm" data-testid="review-gross-row">
                    <span style={{ color: '#5C6B61' }}>Gross Total</span>
                    <span className="font-medium">{'\u20B9'}{gross.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 text-sm" data-testid="review-discount-row">
                    <span style={{ color: '#C05C35' }}>Dealer Discount ({effPct.toFixed(2)}%)</span>
                    <span className="font-medium" style={{ color: '#C05C35' }}>- {'\u20B9'}{discAmt.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 font-bold text-lg border-t" style={{ borderColor: '#E2DFD8' }} data-testid="review-net-row">
                    <span>Net Payable</span>
                    <span style={{ color: '#2B5339' }}>{'\u20B9'}{net.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {reviewOrder && (reviewOrder.status === 'pending' || reviewOrder.status === 'confirmed' || reviewOrder.status === 'partial_dispatched' || ((reviewOrder.status === 'dispatched' || reviewOrder.status === 'delivered') && isStrictAdmin)) && (
        <DialogFooter className="mt-4 flex-row justify-between sm:justify-between">
          <div>
            {(reviewOrder.status === 'confirmed' || reviewOrder.status === 'partial_dispatched') && !reviewOrder.on_hold && canDispatchOrder(reviewOrder) && (
              <Button
                onClick={() => {
                  const o = reviewOrder;
                  onClose();
                  openDispatchDialog(o);
                }}
                style={{ background: '#C05C35' }}
                data-testid="review-dispatch-btn"
              >
                <Truck className="w-4 h-4 mr-2" /> {reviewOrder.status === 'partial_dispatched' ? 'Dispatch rest' : 'Dispatch'}
              </Button>
            )}
            {(reviewOrder.status === 'confirmed' || reviewOrder.status === 'partial_dispatched') && !reviewOrder.on_hold && !canDispatchOrder(reviewOrder) && (
              <div className="text-xs px-3 py-2 rounded" style={{ background: '#FFF5F0', color: '#C05C35', border: '1px solid #E89D4F' }} data-testid="review-dispatch-locked">
                Dispatch unlocks on schedule date ({reviewOrder.delivery_date || '—'})
              </div>
            )}
          </div>
          <div>
            {(reviewOrder.status === 'pending' || reviewOrder.status === 'confirmed' || ((reviewOrder.status === 'dispatched' || reviewOrder.status === 'delivered' || reviewOrder.status === 'partial_dispatched') && isStrictAdmin)) && (
              <Button
                onClick={() => {
                  const o = reviewOrder;
                  onClose();
                  openEditDialog(o);
                }}
                style={{ background: '#2B5339' }}
                data-testid="review-edit-btn"
                title={(reviewOrder.status === 'dispatched' || reviewOrder.status === 'delivered' || reviewOrder.status === 'partial_dispatched') ? 'Admin edit — use with caution' : 'Edit order'}
              >
                <Pencil className="w-4 h-4 mr-2" /> {(reviewOrder.status === 'dispatched' || reviewOrder.status === 'delivered' || reviewOrder.status === 'partial_dispatched') ? 'Edit (Admin)' : 'Edit Order'}
              </Button>
            )}
          </div>
        </DialogFooter>
      )}
    </DialogContent>
  </Dialog>
);
